import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  BarChart3,
  FileText,
  Info,
  Settings as SettingsIcon,
  Store,
  User,
  LogOut,
  ArrowLeft,
  Menu,
  SlidersHorizontal
} from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getClientData } from '../features/portfolio/data/mockData';
import { STORAGE_KEYS } from '../config/constants';

const NAVY = '#0A0A0A';
const CREAM = '#FFFFFF';
const GOLD = '#FFFFFF';
const HAIRLINE = 'rgba(255, 255, 255, 0.10)';

// Wordmark in the app's display serif (Playfair Display) — the same face as the
// browser-tab "B". Collapsed rail shows just the "B" mark; expanded shows the
// full "Banyan" wordmark (no trailing dot).
const BanyanWordmark = ({ collapsed, color }) => (
  <span
    className="font-display font-medium tracking-tight text-xl select-none"
    style={{ color }}
  >
    {collapsed ? 'B' : 'Banyan'}
  </span>
);


const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: clientId } = useParams();
  const { sidebarCollapsed, toggleSidebar, openAIAnalyst, closeAIAnalyst, aiAnalystOpen, openModal } = useUI();
  const { darkMode } = useTheme();
  const { logout } = useAuth();

  const bgColor = darkMode ? NAVY : CREAM;
  const textColor = darkMode ? CREAM : NAVY;
  const hairline = darkMode ? HAIRLINE : 'rgba(0, 0, 0, 0.10)';
  const mutedText = darkMode
    ? 'rgba(255, 255, 255, 0.50)'
    : 'rgba(0, 0, 0, 0.50)';
  const hoverBg = darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)';

  const getCurrentTab = () => {
    if (aiAnalystOpen) return 'analyst';
    const path = location.pathname;
    if (path === '/') return 'portfolios';
    if (path.startsWith('/client/')) return 'portfolios';
    if (path === '/reports') return 'reports';
    if (path === '/marketplace') return 'marketplace';
    if (path === '/manual') return 'manual';
    if (path === '/info') return 'info';
    return null;
  };

  const activeTab = getCurrentTab();
  const clientName = clientId ? getClientData(clientId)?.name || 'Client' : null;
  const isClientDetailView = !!clientId;
  // Authoritative "last business day with data" (MAX as_of_date), stashed at
  // login from the /codenames payload - see config/constants.js. Read only;
  // this never triggers its own network call. Absent -> render nothing.
  const latestDataDate = localStorage.getItem(STORAGE_KEYS.LATEST_DATA_DATE);

  const navigationTabs = [
    { id: 'analyst', label: 'Personal Analyst', icon: User, path: null },
    { id: 'portfolios', label: 'Portfolios', icon: BarChart3, path: '/' },
    { id: 'reports', label: 'Reports', icon: FileText, path: '/reports' },
    { id: 'marketplace', label: 'Market Place', icon: Store, path: '/marketplace' },
    { id: 'manual', label: 'Manual', icon: SlidersHorizontal, path: '/manual' },
    { id: 'info', label: 'Info', icon: Info, path: '/info' },
    { id: 'settings', label: 'Settings', icon: SettingsIcon, path: null },
  ];

  const handleTabClick = (tab) => {
    if (tab.id === 'analyst') {
      openAIAnalyst();
    } else if (tab.id === 'settings') {
      closeAIAnalyst();
      openModal('settings');
    } else if (tab.path) {
      closeAIAnalyst();
      navigate(tab.path);
    }
  };

  return (
    <div
      style={{
        width: sidebarCollapsed ? 64 : 224,
        flexShrink: 0,
        transition: 'width 300ms',
        background: bgColor,
        borderRight: `1px solid ${hairline}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header: wordmark + collapse toggle */}
      <div
        style={{
          padding: sidebarCollapsed ? '16px 12px' : '16px',
          borderBottom: `1px solid ${hairline}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          }}
        >
          {!sidebarCollapsed && <BanyanWordmark collapsed={false} color={textColor} />}
          <button
            onClick={toggleSidebar}
            style={{
              padding: 6,
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: mutedText,
              display: 'flex',
              alignItems: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = hoverBg;
              e.currentTarget.style.color = textColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = mutedText;
            }}
          >
            <Menu size={18} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ padding: '16px', flex: 1 }}>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {navigationTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: sidebarCollapsed ? '8px' : '7px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: isActive
                    ? darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'
                    : 'transparent',
                  color: isActive ? (darkMode ? GOLD : NAVY) : textColor,
                  opacity: isActive ? 1 : 0.7,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: isActive ? 500 : 400,
                  fontFamily: 'Inter, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  gap: 10,
                  transition: 'background 150ms, color 150ms, opacity 150ms',
                  outline: isActive
                    ? `1px solid ${darkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)'}`
                    : 'none',
                  outlineOffset: -1,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = hoverBg;
                    e.currentTarget.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.opacity = '0.7';
                  }
                }}
              >
                <Icon size={sidebarCollapsed ? 20 : 15} />
                {!sidebarCollapsed && <span>{tab.label}</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Footer: client context + logout */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: `1px solid ${hairline}`,
        }}
      >
        {isClientDetailView && !sidebarCollapsed && (
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 13,
                color: mutedText,
                marginBottom: 6,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {clientName}
            </div>
            <button
              onClick={() => { closeAIAnalyst(); navigate('/'); }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: mutedText,
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'Inter, sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = hoverBg;
                e.currentTarget.style.color = textColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = mutedText;
              }}
            >
              <ArrowLeft size={13} />
              <span>Back to Overview</span>
            </button>
          </div>
        )}

        {isClientDetailView && sidebarCollapsed && (
          <button
            onClick={() => { closeAIAnalyst(); navigate('/'); }}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              color: mutedText,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 4,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = hoverBg;
              e.currentTarget.style.color = textColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = mutedText;
            }}
          >
            <ArrowLeft size={18} />
          </button>
        )}

        <button
          onClick={logout}
          style={{
            width: '100%',
            textAlign: 'left',
            padding: sidebarCollapsed ? '8px' : '6px 10px',
            borderRadius: 6,
            border: 'none',
            background: 'transparent',
            color: mutedText,
            cursor: 'pointer',
            fontSize: 13,
            fontFamily: 'Inter, sans-serif',
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            gap: 6,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hoverBg;
            e.currentTarget.style.color = textColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = mutedText;
          }}
        >
          <LogOut size={sidebarCollapsed ? 18 : 13} />
          {!sidebarCollapsed && <span>Logout</span>}
        </button>

        {!sidebarCollapsed && latestDataDate && (
          <button
            onClick={() => { closeAIAnalyst(); navigate('/info'); }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              marginTop: 8,
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: mutedText,
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = textColor; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = mutedText; }}
          >
            Data as of {latestDataDate}
          </button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
